import { useMemo } from "react";
import { z } from "zod";
import {
  useWidgetViewState,
  type WidgetModule,
  type WidgetRenderContext,
} from "@salestrak/ui";
import { IssueViewCustomFields } from "#app/components/issue-view/issue-view-custom-fields";
import { useIssueViewCapabilities } from "#app/components/issue-view/issue-view-capabilities";
import { useCreateIssueMode } from "#app/lib/create-issue-context";
import { getAttachedFieldIds } from "#app/widgets/summary/add-custom-fields-picker";
import { SummaryCustomFieldsEmptyAction } from "#app/widgets/summary/custom-fields-empty-action";
import { useIssueWidgetContext } from "#app/widgets/issue/issue-widget-context";
import { IssueCustomFieldsHeaderActions } from "./header-actions";

export const issueCustomFieldsConfigSchema = z.looseObject({});
export type IssueCustomFieldsConfig = z.infer<typeof issueCustomFieldsConfigSchema>;

/**
 * With no fields on the Project, the body is the one "Add fields" action the
 * card lifts into its header (see `WidgetEmptyAction`) — for a viewer who can
 * add one. A create draft adds fields the same way (the added field's default
 * lands in the draft's values); a read-only viewer cannot write that default,
 * so it keeps the plain body.
 */
function IssueCustomFieldsRender(_: WidgetRenderContext<IssueCustomFieldsConfig>) {
  const ctx = useIssueWidgetContext();
  const createCtx = useCreateIssueMode();
  const { canEdit } = useIssueViewCapabilities();
  const { state } = useWidgetViewState();
  const search = typeof state.search === "string" ? state.search : "";
  const fieldIds = useMemo(
    () =>
      getAttachedFieldIds({
        values: ctx.values,
        orderedTypeFieldIds: ctx.orderedTypeFieldIds,
        alwaysShownFieldIds: ctx.alwaysShownFieldIds,
      }),
    [ctx.values, ctx.orderedTypeFieldIds, ctx.alwaysShownFieldIds],
  );
  if ((createCtx || canEdit) && fieldIds.length === 0) {
    return (
      <SummaryCustomFieldsEmptyAction
        fieldIds={fieldIds}
        customFields={ctx.customFields}
        onUpdateValue={ctx.onUpdateValue}
        locationName={createCtx ? createCtx.draft.name : ctx.issue?.name}
      />
    );
  }
  return (
    <IssueViewCustomFields
      issue={ctx.issue}
      values={ctx.values}
      customFields={ctx.customFields}
      orderedTypeFieldIds={ctx.orderedTypeFieldIds}
      onUpdateValue={ctx.onUpdateValue}
      onAddPhoto={ctx.onAddPhoto}
      onRemovePhoto={ctx.onRemovePhoto}
      requiredFieldIds={ctx.requiredFieldIds}
      alwaysShownFieldIds={ctx.alwaysShownFieldIds}
      onCloseIssue={ctx.onCloseIssue}
      focusCustomFieldId={ctx.focusCustomFieldId}
      hideHeader
      search={search}
    />
  );
}

export const issueCustomFieldsDef: WidgetModule<IssueCustomFieldsConfig> = {
  type: "issue-custom-fields",
  title: "Custom Fields",
  icon: "swatch-book",
  // The rows lead with their own gutter — see `WidgetModule.leadingRail`.
  leadingRail: true,
  span: { minW: 3, minH: 1, maxW: 12, maxH: 12, defaultW: 12, defaultH: 4 },
  configSchema: issueCustomFieldsConfigSchema,
  headerActions: IssueCustomFieldsHeaderActions,
  render: IssueCustomFieldsRender,
};
